import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ChatWindowService } from 'src/app/shared/services/chat-window.service';
import { ChatService } from 'src/app/shared/services/chat.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import {
  AppConstant,
  TranslationData,
} from 'src/app/shared/utilities/app.constant';

/**
 * Component for handling confirmation buttons in chat messages
 * Supports both regular button interactions and special buttons like "chat with human"
 */
@Component({
    selector: 'app-chat-message-confirm',
    templateUrl: './chat-message-confirm.component.html',
    styleUrls: ['./chat-message-confirm.component.scss'],
    imports: [NgFor, NgClass, NgIf, SvgIconComponent]
})
export class ChatMessageConfirmComponent implements OnInit {
  @Input() message: AppConstant.Message;
  @Input() translationData: TranslationData;

  isInteractionDisabled: boolean = false;
  content: any;
  isShowingTranslation: boolean = false;
  isTranslating: boolean = false;

  constructor(
    private chatService: ChatService,
    private chatWindowService: ChatWindowService,
    private userInfoService: UserInfoService
  ) {}

  ngOnInit(): void {
    this.content = this.message.content;
    this.isShowingTranslation = this.translationData?.hasTranslation;

    if (this.translationData?.hasTranslation) {
      this.content = this.translationData.translatedContent;
    }

    // Check if any button is already selected
    for (const button of this.content?.buttons || []) {
      if (button.selected) {
        this.isInteractionDisabled = true;
        return;
      }
    }
  }

  onClickTranslation() {
    if (this.translationData?.translatedContent) {
      this.isShowingTranslation = !this.isShowingTranslation;

      if (this.isShowingTranslation) {
        this.content = this.translationData.translatedContent;
      } else {
        this.content = this.message.content;
      }
    } else if (this.translationData?.userPreferredLanguage) {
      // Call API to translate the message
      this.isTranslating = true;
      this.chatService
        .translateMessage(
          this.message.id,
          this.translationData.userPreferredLanguage
        )
        .subscribe(() => {
          this.isTranslating = false;
          this.isShowingTranslation = true;
        });
    }
  }

  /**
   * Handles button clicks in confirmation messages
   * Special case: When "chat with human" or similar buttons are clicked,
   * this will notify agents via webhook
   */
  onButtonClick(index: number) {
    if (this.isInteractionDisabled) return;
    this.isInteractionDisabled = true;

    const buttonLabel = this.content.buttons[index].label;
    const updatedContent = this.message.content;
    updatedContent.buttons[index].selected = true;

    // Check if this is a button to request live agent support
    const isLiveAgentRequest =
      buttonLabel.toLowerCase().includes('human') ||
      buttonLabel.toLowerCase().includes('chat with a human') ||
      buttonLabel.toLowerCase().includes('agent');

    if (isLiveAgentRequest) {
      // Change to LIVE support step
      this.chatWindowService.setCurrentStep('LIVE');

      // Send notification using the unified function
      this.userInfoService.userSubject.subscribe(userInfo => {
        if (!userInfo) return;

        const context = `User clicked "${buttonLabel}" button`;
        this.chatService.sendAgentNotification(userInfo, context).subscribe();
      });
    }

    // Update the message to show the button as selected
    this.chatService
      .updateInteractionMesssage(
        this.message.id,
        updatedContent,
        this.content.buttons[index].label
      )
      .subscribe();
  }
}
